import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Landmark, Coins, Smartphone, ArrowUpRight, ArrowDownRight, Link2, CheckCircle2 } from "lucide-react";

const GROUP_ICONS = { bank: Landmark, cash: Coins, mobile_money: Smartphone };
const PROVIDERS = [
  { code: "orange_money", name: "Orange Money" }, { code: "moov_money", name: "Moov Money" },
  { code: "wave_money", name: "Wave" }, { code: "mtn_momo", name: "MTN MoMo" },
  { code: "airtel_money", name: "Airtel Money" }, { code: "bank", name: "Virement bancaire" },
];

export default function Treasury() {
  const { companyId, cur } = useCompany();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [amount, setAmount] = useState("");
  const [provider, setProvider] = useState("orange_money");
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/treasury", { params: { company_id: companyId } }).then(({ data }) => setData(data));
    api.get("/money/summary", { params: { company_id: companyId } }).then(({ data }) => setSummary(data));
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const findMatch = async () => {
    if (!amount) return;
    setBusy(true);
    try {
      const { data } = await api.get("/money/match", { params: { company_id: companyId, amount: parseFloat(amount) } });
      setMatches(data.matches);
      if (!data.matches.length) toast.info("Aucune facture ouverte ne correspond.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const reconcile = async (m) => {
    try {
      await api.post("/money/reconcile", { invoice_id: m.invoice_id, amount: parseFloat(amount), provider }, { params: { company_id: companyId } });
      toast.success(`Rapproché avec ${m.number} ✓`);
      setMatches(null); setAmount(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data || !summary) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="treasury-page">
      <PageHeader title="NATORIQ Money" subtitle="Banques, Mobile Money et caisse — avec rapprochement automatique." />

      <Card className="p-5 rounded-md border-border shadow-sm mb-5">
        <div className="text-sm text-muted-foreground">Trésorerie totale disponible</div>
        <div className="font-display text-3xl font-bold tabular"><Money value={summary.total} cur={cur} /></div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {summary.groups.map((g) => {
          const Icon = GROUP_ICONS[g.kind] || Coins;
          return (
            <Card key={g.kind} className="p-4 rounded-md border-border shadow-sm" data-testid={`money-group-${g.kind}`}>
              <div className="flex items-center gap-2 text-sm font-semibold"><span className="w-8 h-8 rounded-md bg-accent flex items-center justify-center text-primary"><Icon className="w-4 h-4" /></span>{g.label}</div>
              <div className="font-display text-xl font-bold mt-2 tabular"><Money value={g.total} cur={cur} /></div>
              <div className="mt-2 space-y-1">
                {g.accounts.map((a) => (
                  <div key={a.id} className="flex justify-between text-xs text-muted-foreground"><span>{a.label}</span><Money value={a.balance} cur={cur} /></div>
                ))}
              </div>
            </Card>);
        })}
      </div>

      <Card className="p-5 rounded-md border-primary/30 bg-accent/30 shadow-sm mb-6" data-testid="reconcile-tool">
        <div className="flex items-center gap-2 font-display font-semibold mb-3"><Link2 className="w-5 h-5 text-primary" />Rapprochement automatique</div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label>Montant reçu</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="75000" className="w-40" data-testid="reconcile-amount" /></div>
          <div className="space-y-1"><Label>Canal</Label>
            <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="reconcile-provider">
              {PROVIDERS.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
            </select>
          </div>
          <Button onClick={findMatch} disabled={busy || !amount} className="rounded-full" data-testid="reconcile-find">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Trouver la correspondance"}</Button>
        </div>
        {matches && matches.length > 0 && (
          <div className="mt-4 space-y-2">
            {matches.map((m) => (
              <div key={m.invoice_id} className="flex items-center justify-between gap-3 bg-white rounded-md border border-border px-3 py-2.5" data-testid={`match-${m.invoice_id}`}>
                <span className="text-sm">
                  <span className="font-medium">{m.number}</span> · {m.customer_name} · reste <Money value={m.remaining} cur={cur} />
                  <span className={`ml-2 text-xs font-semibold ${m.score >= 95 ? "text-emerald-600" : "text-amber-600"}`}>correspondance {m.score} %</span>
                </span>
                <Button size="sm" className="rounded-full shrink-0" onClick={() => reconcile(m)} data-testid={`reconcile-apply-${m.invoice_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Rapprocher</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="rounded-md border-border shadow-sm overflow-hidden">
        <div className="px-5 py-3 font-display font-semibold border-b border-border">Mouvements récents</div>
        {data.transactions.length === 0 ? <p className="p-6 text-sm text-muted-foreground">Aucun mouvement.</p>
          : <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Compte</TableHead><TableHead>Libellé</TableHead><TableHead className="text-right">Montant</TableHead></TableRow></TableHeader>
              <TableBody>{data.transactions.map((t) => (
                <TableRow key={t.id} data-testid={`txn-row-${t.id}`}>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(t.occurred_at)}</TableCell>
                  <TableCell>{t.account_label}</TableCell>
                  <TableCell className="text-sm">{t.description}</TableCell>
                  <TableCell className={`text-right font-semibold ${t.direction === "in" ? "text-emerald-600" : "text-rose-600"}`}>
                    <span className="inline-flex items-center gap-1">{t.direction === "in" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}<Money value={t.amount} cur={cur} /></span>
                  </TableCell>
                </TableRow>))}
              </TableBody>
            </Table>}
      </Card>
    </div>
  );
}
